'use client';

import { useState } from 'react';
import ContactCTA from '@/components/ContactCTA';
import ContactNew from '@/components/ContactNew';
import Content from '@/components/Content';
import FrequentlyAskedQuestions from '@/components/FrequentlyAskedQuestions';
import Header from '@/components/Header';
import Pagination from '@/components/Pagination';
import { Markdown } from '@/lib/markdown';
import { liveSolutions, solutionHref } from '@/lib/routes';
import IWarehouseSolutionsPage, {
	IWarehousingSolutionsResponse,
} from '@/models/warehousingSolution';

export default function WarehousingSolutionsPage(props: {
	warehousingSolutionsPage: IWarehouseSolutionsPage;
	featuredWarehousingSolution: IWarehousingSolutionsResponse;
	warehousingSolutions: IWarehousingSolutionsResponse;
}) {
	'use client';

	const [pageNum, setPageNum] = useState(0);
	const perPage = 8;

	const warehousingSolutionsPage = props.warehousingSolutionsPage;

	if (!warehousingSolutionsPage) {
		return null;
	}

	const { warehousingSolutions } = props;
	// Retired Solutions still come back from Contentful but only 301 elsewhere.
	const listedSolutions = liveSolutions(warehousingSolutions?.services);
	const paginatedSolutions = listedSolutions.slice(pageNum * perPage, (pageNum + 1) * perPage);

	return (
		<section className="-mt-16">
			<div className="z-20 relative bg-contain xl:bg-cover bg-white text-white bg-bottom  flex flex-col">
				<Header
					small
					title={warehousingSolutionsPage?.title}
					headerSliderImagesCollection={{ items: [warehousingSolutionsPage?.image] }}
				/>
			</div>

			<div className="relative black py-12 md:pb-16 bg-transparent flex flex-col gap:14 md:gap-28 bg-white">
				<Content
					reverseOnMobile
					className="[&_h2]:text-3xl [&_h2]:sm:text-4xl [&_h2]:md:text-5xl border-b-2 pb-16 lg:pb-32 lg:pt-10"
					xlTitle="Featured solution"
					title={props.featuredWarehousingSolution?.services?.[0]?.title}
					image={props.featuredWarehousingSolution?.services?.[0]?.cardImage}
					imageCover="cover"
					buttons={[
						{
							link: solutionHref(props?.featuredWarehousingSolution?.services?.[0]),
							text: `View ${props.featuredWarehousingSolution?.services?.[0]?.title}`,
							color: 'dark',
							variant: 'outlined',
						},
					]}
				/>

				<div className="flex flex-col gap-12 md:gap-32  overflow-hidden mt-16 lg:mt-0">
					{paginatedSolutions?.map((service, index) => {
						return (
							<Content
								key={service.slug}
								description={service.excerpt}
								title={service?.title}
								image={service?.cardImage}
								buttons={[
									{
										link: solutionHref(service),
										text: `View ${service?.title}`,
										color: 'dark',
										variant: 'outlined',
									},
								]}
								imageCover="cover"
								reverse={index % 2 === 0}
							/>
						);
					})}
				</div>
			</div>

			<div className="pb-16 md:pb-32 bg-white flex">
				<Pagination
					defaultPerPage={perPage}
					currentPage={pageNum}
					totalItems={listedSolutions.length}
					onPageChange={(page) => {
						setPageNum(page);
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}}
				/>
			</div>

			<ContactCTA
				title={warehousingSolutionsPage.callToAction?.title}
				description={
					warehousingSolutionsPage.callToAction?.contentDescription && (
						<Markdown
							content={warehousingSolutionsPage.callToAction.contentDescription}
						/>
					)
				}
				buttons={warehousingSolutionsPage.callToAction?.buttonsCollection?.items}
			/>

			{!!warehousingSolutionsPage?.faqsCollection?.items?.length ? (
				<div className="z-10 relative py-12  md:py-32 flex flex-col overflow-clip bg-white">
					<FrequentlyAskedQuestions
						faqs={warehousingSolutionsPage?.faqsCollection?.items}
						title={'Frequently Asked Questions'}
					/>
				</div>
			) : null}
		</section>
	);
}
